import type { VersionLine } from './version-lines.js'

export interface DiffPart {
  text: string
  /** A word that differs from the other form of a changed line, shown emphasised */
  changed: boolean
}

export interface DiffRow extends VersionLine {
  kind: 'same' | 'added' | 'removed'
  /** Set on the two forms of a changed line, so the changed words can be emphasised */
  parts?: DiffPart[]
}

export interface CollapsedRows {
  kind: 'collapsed'
  rows: DiffRow[]
}

export type DiffItem = DiffRow | CollapsedRows

type Op = 'same' | 'added' | 'removed'

/** The longest-common-subsequence edit script between two sequences, in order */
const editScript = <T>(a: T[], b: T[], equal: (x: T, y: T) => boolean): { op: Op; item: T }[] => {
  const n = a.length
  const m = b.length
  /** lcs[i][j] = length of the LCS of a[i..] and b[j..] */
  const lcs = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0))
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] = equal(a[i], b[j]) ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1])
    }
  }
  const script: { op: Op; item: T }[] = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (equal(a[i], b[j])) {
      script.push({ op: 'same', item: b[j] })
      i += 1
      j += 1
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      script.push({ op: 'removed', item: a[i] })
      i += 1
    } else {
      script.push({ op: 'added', item: b[j] })
      j += 1
    }
  }
  a.slice(i).forEach(item => script.push({ op: 'removed', item }))
  b.slice(j).forEach(item => script.push({ op: 'added', item }))
  return script
}

/** Words and the spaces between them as separate parts, so the parts join back into the line exactly */
const words = (text: string) => text.match(/\S+|\s+/g) ?? []

const isSpace = (part: string) => !part.trim()

const sameLine = (x: VersionLine, y: VersionLine) => x.text === y.text && Boolean(x.heading) === Boolean(y.heading)

/** The changed words of each form of a changed line */
const wordParts = (removed: string, added: string): { removed: DiffPart[]; added: DiffPart[] } => {
  const script = editScript(words(removed), words(added), (x, y) => x === y)
  /** Only words are emphasised; a space is never a change worth marking */
  return {
    removed: script
      .filter(s => s.op !== 'added')
      .map(s => ({ text: s.item, changed: s.op === 'removed' && !isSpace(s.item) })),
    added: script
      .filter(s => s.op !== 'removed')
      .map(s => ({ text: s.item, changed: s.op === 'added' && !isSpace(s.item) })),
  }
}

/**
 * The line changes from one version to the next (GH #2695 spec §2). A changed line reads as its removed form, then its
 * added form, with the changed words marked: within a run of removed and added lines, they pair up in order.
 */
export const diffVersionLines = (before: VersionLine[], after: VersionLine[]): DiffRow[] => {
  const script = editScript(before, after, sameLine)
  const rows: DiffRow[] = []
  let k = 0
  while (k < script.length) {
    if (script[k].op === 'same') {
      rows.push({ ...script[k].item, kind: 'same' })
      k += 1
      continue
    }
    const removed: VersionLine[] = []
    const added: VersionLine[] = []
    while (k < script.length && script[k].op !== 'same') {
      ;(script[k].op === 'removed' ? removed : added).push(script[k].item)
      k += 1
    }
    const pairs = Math.min(removed.length, added.length)
    for (let p = 0; p < pairs; p++) {
      const parts = wordParts(removed[p].text, added[p].text)
      rows.push({ ...removed[p], kind: 'removed', parts: parts.removed })
      rows.push({ ...added[p], kind: 'added', parts: parts.added })
    }
    removed.slice(pairs).forEach(line => rows.push({ ...line, kind: 'removed' }))
    added.slice(pairs).forEach(line => rows.push({ ...line, kind: 'added' }))
  }
  return rows
}

/**
 * Unchanged lines collapse into one row each run (spec §2): two lines of context stay around every change, and so does
 * the section heading a change is in, so you can tell where it is.
 */
export const collapseUnchanged = (rows: DiffRow[], context = 2): DiffItem[] => {
  const visible = new Set<number>()
  let lastHeading = -1
  rows.forEach((row, index) => {
    if (row.heading) {
      lastHeading = index
    }
    if (row.kind === 'same') {
      return
    }
    for (let c = Math.max(0, index - context); c <= Math.min(rows.length - 1, index + context); c++) {
      visible.add(c)
    }
    if (lastHeading >= 0) {
      visible.add(lastHeading)
    }
  })

  const items: DiffItem[] = []
  rows.forEach((row, index) => {
    if (visible.has(index)) {
      items.push(row)
      return
    }
    const previous = items.at(-1)
    if (previous?.kind === 'collapsed') {
      previous.rows.push(row)
      return
    }
    items.push({ kind: 'collapsed', rows: [row] })
  })
  return items
}

export const hasLineChanges = (rows: DiffRow[]) => rows.some(row => row.kind !== 'same')
